import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { ExerciseResponse } from '@shared/schemas/exercise'
import { Button } from '@web/components/ui/Button'
import { FillBlankExercise } from '@web/features/lesson/exercises/FillBlankExercise'
import { MatchingExercise } from '@web/features/lesson/exercises/MatchingExercise'
import { MultipleChoiceExercise } from '@web/features/lesson/exercises/MultipleChoiceExercise'
import { TranslationExercise } from '@web/features/lesson/exercises/TranslationExercise'
import { hasAnswer } from '@web/features/lesson/exercises/types'
import { WordOrderExercise } from '@web/features/lesson/exercises/WordOrderExercise'
import {
  useAnswerPlacement,
  useFinishPlacement,
  useStartPlacement,
  type PlacementItem,
  type PlacementSummary,
} from '@web/lib/placement'

/**
 * The placement test.
 *
 * Two things make this different from a lesson, and both are deliberate:
 * there is **no feedback between questions** (feedback would teach, and a test
 * that teaches cannot measure), and skipping is allowed — "I don't know" is a
 * real answer and far more useful than a guess.
 */
export function PlacementTest() {
  const navigate = useNavigate()
  const start = useStartPlacement()
  const answer = useAnswerPlacement()
  const finish = useFinishPlacement()

  const [items, setItems] = useState<PlacementItem[]>([])
  const [attemptId, setAttemptId] = useState<string | null>(null)
  const [index, setIndex] = useState(0)
  const [response, setResponse] = useState<ExerciseResponse | null>(null)
  const [summary, setSummary] = useState<PlacementSummary | null>(null)

  const item = items[index]

  async function begin() {
    const result = await start.mutateAsync()
    setItems(result.items)
    setAttemptId(result.attemptId)
    setIndex(0)
  }

  async function submit(skipped: boolean) {
    if (!attemptId || !item) return

    let stopHere = false
    if (!skipped && response) {
      const result = await answer.mutateAsync({ attemptId, itemId: item.id, response })
      stopHere = result.stopHere
    }

    setResponse(null)
    const next = index + 1

    // The server owns the stop rule; the client just honours it. A skipped
    // question counts as unanswered, not as wrong.
    if (stopHere || next >= items.length) {
      const result = await finish.mutateAsync({ attemptId })
      setSummary(result)
      return
    }
    setIndex(next)
  }

  if (summary) return <Results summary={summary} onDone={() => void navigate('/')} />

  if (!attemptId) {
    return (
      <Screen>
        <h1 className="text-2xl font-bold">Where should you start?</h1>
        <p className="text-ink-700">
          They start easy and get harder, and stop as soon as they have found your
          level — usually under five minutes, longer only if you keep passing.
        </p>
        <p className="text-ink-700">
          Skip anything you do not know — guessing makes the result worse, not better.
          Whatever you already know gets marked done, and you can still open any of it to
          review.
        </p>
        <div className="flex w-full flex-col gap-2">
          <Button size="lg" onClick={() => void begin()} loading={start.isPending}>
            Start the test
          </Button>
          <Button variant="ghost" onClick={() => void navigate('/')}>
            Skip — start from the beginning
          </Button>
        </div>
        {start.isError && (
          <p role="alert" className="text-sm text-red-700">
            Could not start the test. Check your connection and try again.
          </p>
        )}
      </Screen>
    )
  }

  if (!item || finish.isPending) {
    return (
      <Screen>
        <span
          aria-label="Working out your level"
          className="size-8 animate-spin rounded-full border-3 border-brand-600 border-t-transparent"
        />
      </Screen>
    )
  }

  const busy = answer.isPending || finish.isPending

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-10 bg-sand-50/90 px-4 pt-[max(0.75rem,var(--safe-top))] pb-3 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <span className="text-sm text-ink-500 tabular-nums">
            {index + 1} / {items.length}
          </span>
          <div
            className="h-2.5 flex-1 overflow-hidden rounded-full bg-sand-200"
            role="progressbar"
            aria-valuenow={index + 1}
            aria-valuemin={0}
            aria-valuemax={items.length}
            aria-label="Test progress"
          >
            <div
              className="h-full rounded-full bg-brand-600 transition-[width] duration-300"
              style={{ width: `${((index + 1) / items.length) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-4">
        <h2 className="mb-5 text-xl font-semibold text-ink-900">{item.prompt}</h2>
        <ItemInput item={item} response={response} onChange={setResponse} />
      </main>

      <footer className="sticky bottom-0 border-t border-sand-200 bg-white px-4 pt-3 pb-[max(0.75rem,var(--safe-bottom))]">
        <div className="mx-auto flex max-w-lg flex-col gap-2">
          <Button
            size="lg"
            onClick={() => void submit(false)}
            disabled={!hasAnswer(response) || busy}
            loading={busy}
          >
            Next
          </Button>
          <Button variant="ghost" onClick={() => void submit(true)} disabled={busy}>
            I don&rsquo;t know this
          </Button>
        </div>
      </footer>
    </div>
  )
}

function ItemInput({
  item,
  response,
  onChange,
}: {
  item: PlacementItem
  response: ExerciseResponse | null
  onChange: (r: ExerciseResponse) => void
}) {
  const { content } = item
  // `locked` is always false and `result` always null: the test never reveals
  // whether she was right, so the renderers stay in their neutral state.
  const shared = { locked: false, result: null }

  switch (content.type) {
    case 'multiple_choice':
      return (
        <MultipleChoiceExercise
          {...shared}
          content={content}
          response={response?.type === 'multiple_choice' ? response : null}
          onChange={onChange}
        />
      )
    case 'fill_blank':
      return (
        <FillBlankExercise
          {...shared}
          content={content}
          response={response?.type === 'fill_blank' ? response : null}
          onChange={onChange}
        />
      )
    case 'translation':
      return (
        <TranslationExercise
          {...shared}
          content={content}
          response={response?.type === 'translation' ? response : null}
          onChange={onChange}
        />
      )
    case 'word_order':
      return (
        <WordOrderExercise
          {...shared}
          content={content}
          response={response?.type === 'word_order' ? response : null}
          onChange={onChange}
        />
      )
    case 'matching':
      return (
        <MatchingExercise
          {...shared}
          content={content}
          response={response?.type === 'matching' ? response : null}
          onChange={onChange}
        />
      )
    default:
      // Listening and speaking items need audio, which the bank does not use.
      return <p className="text-ink-500">This question type is not available yet.</p>
  }
}

const UNIT_LABELS: Record<string, string> = {
  'pronunciation-greetings': 'Pronunciation and Greetings',
  'introducing-yourself': 'Introducing Yourself',
  'family-conversations': 'Family Conversations',
  'essential-verbs': 'Essential Verbs',
  'restaurants-food': 'Restaurants and Food',
  'shopping-money': 'Shopping and Money',
  transportation: 'Transportation',
  'daily-conversations': 'Daily Conversations',
  'brazilian-expressions': 'Brazilian Expressions',
  'travel-preparation': 'Travel Preparation',
}

function Results({ summary, onDone }: { summary: PlacementSummary; onDone: () => void }) {
  const startLabel = summary.startUnitSlug
    ? (UNIT_LABELS[summary.startUnitSlug] ?? summary.startUnitSlug)
    : null

  return (
    <main className="mx-auto min-h-dvh max-w-lg px-5 pt-[max(2rem,var(--safe-top))] pb-[max(2rem,var(--safe-bottom))]">
      <p className="text-5xl" aria-hidden="true">
        🧭
      </p>
      <h1 className="mt-3 text-2xl font-bold">Here is where you stand</h1>
      <p className="mt-1 text-ink-700">
        {summary.itemsCorrect} of {summary.itemsSeen} correct · level {summary.estimatedLevel}
      </p>

      {startLabel && (
        <div className="mt-5 rounded-card bg-brand-600 p-5 text-white">
          <p className="text-sm text-brand-100">Start here</p>
          <p className="mt-1 text-xl font-semibold">{startLabel}</p>
        </div>
      )}

      <ul className="mt-5 space-y-2">
        {summary.unitScores.map((score) => (
          <li
            key={score.unitSlug}
            className="flex items-center gap-3 rounded-card bg-white px-4 py-3 ring-1 ring-brand-900/5"
          >
            <span aria-hidden="true">{score.passed ? '✅' : '📘'}</span>
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-ink-900">
                {UNIT_LABELS[score.unitSlug] ?? score.unitSlug}
              </span>
              <span className="block text-sm text-ink-500">
                {score.itemsCorrect}/{score.itemsSeen} · {score.passed ? 'skipped' : 'you will study this'}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {summary.lessonsSkipped > 0 && (
        <p className="mt-4 text-sm text-ink-700">
          {summary.lessonsSkipped} lesson{summary.lessonsSkipped === 1 ? '' : 's'} marked as done
          and {summary.wordsSeeded} word{summary.wordsSeeded === 1 ? '' : 's'} added to your
          reviews. Nothing is hidden — you can still open anything to look at it.
        </p>
      )}

      {summary.passedUnits.length > summary.unitsApplied.length && (
        <p className="mt-3 text-sm text-ink-500">
          Some units you passed are still being written. They will be marked done
          automatically when they arrive — you will not have to retake this.
        </p>
      )}

      <Button size="lg" className="mt-6 w-full" onClick={onDone}>
        Start learning
      </Button>
    </main>
  )
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </main>
  )
}
